/**
 * Validation Utilities
 * Single Responsibility: Input validation and sanitization
 */

/**
 * Validate session ID format (UUID v4)
 */
export const isValidSessionId = (id: string | undefined): boolean => {
  if (!id) return false
  
  // UUID v4 format validation
  const uuidV4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  
  // Also accept legacy formats for backward compatibility
  const legacyFormatRegex = /^session-[0-9a-zA-Z]{8,}$/
  
  // For development/testing: accept any non-empty string that looks like an ID
  const developmentFormatRegex = /^[a-zA-Z0-9_-]+$/
  
  return uuidV4Regex.test(id) || legacyFormatRegex.test(id) || developmentFormatRegex.test(id)
}

/**
 * Sanitize client name to prevent XSS
 */
export const sanitizeClientName = (name: string | undefined): string => {
  if (!name) return 'Client'
  
  // Remove any HTML tags and special characters
  const sanitized = name
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/[<>"'&]/g, '') // Remove dangerous characters
    .trim()
    .substring(0, 100) // Limit length
  
  return sanitized || 'Client'
}

/**
 * Validate email format
 */
export const isValidEmail = (email: string | undefined): boolean => {
  if (!email) return false
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Validate client type
 */
export type ClientType = 'retail' | 'professional' | 'institutional'

export const isValidClientType = (type: string | undefined): type is ClientType => {
  return type === 'retail' || type === 'professional' || type === 'institutional'
}

/**
 * Get client type from session data with validation
 */
export const getValidatedClientType = (sessionData: any): ClientType => {
  const type = sessionData?.clientType
  
  if (isValidClientType(type)) {
    return type
  }
  
  // Default based on portfolio size if available
  const portfolioValue = sessionData?.portfolioValue
  if (portfolioValue) {
    if (portfolioValue >= 10000000) return 'institutional'
    if (portfolioValue >= 500000) return 'professional'
  }
  
  // Conservative default for compliance
  return 'retail'
}
